/**
 * describeコマンドハンドラ
 *
 * リソース（データセット、タブ、モデル、レポート）の詳細情報を取得
 * IDから自動的にリソースタイプを判定する
 *
 * 使用例:
 * x:describe {"id":"dataset_1"}
 * x:describe {"id":"tab_123"}
 * x:describe {"id":"model_1"}
 * x:describe {"id":"report_1"}
 */

import type { CommandHandler, CommandResult, ParsedCommand, CommandContext } from '../types';
import type { GLMParameters } from '../../../types/model.types';
import logger from '../../utils/logger';

/**
 * x:describeコマンドで受け付けるJSON形式
 */
interface DescribeInput {
  id: string;
}

export const describeHandler: CommandHandler = {
  name: 'describe',
  description: 'Get detailed information about a resource (dataset, tab, model, or report)',
  category: 'utility',
  parameters: [
    {
      name: 'json',
      type: 'string',
      required: false,
      description: 'JSON object with id'
    }
  ],
  aliases: [],
  examples: [
    'x:describe {"id":"dataset_1"}',
    'x:describe {"id":"tab_123"}',
    'x:describe {"id":"model_1"}',
    'x:describe {"id":"report_1"}'
  ],

  execute: async (args: ParsedCommand, context: CommandContext): Promise<CommandResult> => {
    const jsonStr = args.positionalArgs.join(' ').trim();

    if (!jsonStr) {
      return {
        success: false,
        message: 'Resource ID required',
        error: {
          code: 'NO_JSON',
          message: 'No resource ID specified',
          suggestion: 'Provide ID: x:describe {"id":"dataset_1"}'
        }
      };
    }

    let input: DescribeInput;
    try {
      input = JSON.parse(jsonStr) as DescribeInput;
    } catch (e) {
      return {
        success: false,
        message: 'Invalid JSON format',
        error: {
          code: 'INVALID_JSON',
          message: `Failed to parse JSON: ${e instanceof Error ? e.message : String(e)}`,
          suggestion: 'Check JSON syntax'
        }
      };
    }

    if (!input.id) {
      return {
        success: false,
        message: 'Resource ID required',
        error: {
          code: 'NO_ID',
          message: 'No resource ID specified',
          suggestion: 'Use x:list to see available resources'
        }
      };
    }

    // データセットを検索
    const dataset = context.datasets[input.id];
    if (dataset) {
      const columns = dataset.schema.columns.map(col => ({
        name: col.name,
        type: col.type,
        scale: col.scale
      }));

      const description = {
        resourceType: 'dataset',
        id: input.id,
        name: dataset.name,
        type: dataset.type,
        rows: dataset.rowCount ?? 0,
        columns,
        ...(dataset.type === 'derived' ? {
          parentIds: dataset.parentDatasetIds,
          operation: dataset.operation.type
        } : {}),
        ...(dataset.type === 'primary' ? {
          originalFileName: dataset.metadata.originalFileName
        } : {})
      };

      logger.info(`[AgentCommand] Describe dataset ${input.id}`, description);
      return {
        success: true,
        message: `Dataset: ${dataset.name}`,
        data: description
      };
    }

    // タブを検索
    const tabs = context.getTabs();
    const tab = tabs.find(t => t.id === input.id);
    if (tab) {
      const description = {
        resourceType: 'tab',
        id: tab.id,
        type: tab.type,
        title: tab.title,
        containerId: tab.containerId
      };

      logger.info(`[AgentCommand] Describe tab ${input.id}`, description);
      return {
        success: true,
        message: `Tab: ${tab.title}`,
        data: description
      };
    }

    // モデルを検索
    const models = context.getModels();
    const model = models.find(m => m.id === input.id);
    if (model) {
      const description = {
        resourceType: 'model',
        id: model.id,
        type: model.type,
        name: model.name,
        datasetId: model.metadata.trainingDatasetId,
        features: model.metadata.features,
        target: model.metadata.target,
        sampleSize: model.metadata.sampleSize,
        createdAt: model.metadata.createdAt,
        ...(model.type === 'glm' && 'family' in model.parameters ? {
          family: (model.parameters as GLMParameters).family,
          link: (model.parameters as GLMParameters).link,
          converged: (model.parameters as GLMParameters).converged,
          aic: (model.parameters as GLMParameters).aic,
          bic: (model.parameters as GLMParameters).bic
        } : {})
      };

      logger.info(`[AgentCommand] Describe model ${input.id}`, description);
      return {
        success: true,
        message: `Model: ${model.name}`,
        data: description
      };
    }

    // レポートを検索
    const reports = context.getReports();
    const report = reports.find(r => r.id === input.id);
    if (report) {
      const elements = Object.values(report.elements).map(el => ({
        id: el.id,
        type: el.type,
        ...('title' in el && el.title ? { title: el.title } : {})
      }));

      const description = {
        resourceType: 'report',
        id: report.id,
        name: report.name,
        elementCount: Object.keys(report.elements).length,
        elements
      };

      logger.info(`[AgentCommand] Describe report ${input.id}`, description);
      return {
        success: true,
        message: `Report: ${report.name}`,
        data: description
      };
    }

    // どのリソースにも見つからない
    return {
      success: false,
      message: `Resource not found: ${input.id}`,
      error: {
        code: 'RESOURCE_NOT_FOUND',
        message: `Resource "${input.id}" does not exist`,
        suggestion: 'Use x:list to see available resources'
      }
    };
  }
};
